import React, { useState, useEffect } from 'react';
import './PurchaseModal.css';
import { authService } from '../services/authService';

interface PricingProduct {
    currency_options?: Record<string, { unit_amount: number | null }>;
}

interface PricingData {
    products: Record<string, PricingProduct>;
    detected_currency: string;
    supported_currencies: string[];
}

const currencySymbols: Record<string, string> = {
    USD: '$',
    EUR: '€',
    JPY: '¥',
    CNY: '¥',
    AUD: '$',
    GBP: '£',
};

const isPricingData = (data: unknown): data is PricingData => {
    if (!data || typeof data !== 'object') {
        return false;
    }
    const candidate = data as Partial<PricingData>;
    const hasProducts = typeof candidate.products === 'object' && candidate.products !== null;
    const hasSupportedCurrencies = Array.isArray(candidate.supported_currencies);
    return hasProducts && hasSupportedCurrencies;
};

const getDynamicProducts = (selectedCurrency: string, pricingData: PricingData | null): Product[] => {
    const currencySymbol = currencySymbols[selectedCurrency] || '$';

    const getPrice = (productId: string): string => {
        // First try to get actual price from Stripe API data
        if (pricingData && pricingData.products[productId]) {
            const productData = pricingData.products[productId];

            // Try to get currency-specific price from currency_options (note: Stripe returns lowercase keys)
            const lowerCaseCurrency = selectedCurrency.toLowerCase();
            if (productData.currency_options && productData.currency_options[lowerCaseCurrency]) {
                const unitAmount = productData.currency_options[lowerCaseCurrency].unit_amount;
                console.log(`Using converted price for ${productId} in ${selectedCurrency}:`, unitAmount);

                // Check if unit_amount is valid (not null/undefined)
                if (unitAmount !== null && unitAmount !== undefined) {
                    // Handle zero-decimal currencies like JPY
                    if (selectedCurrency === 'JPY') {
                        return unitAmount.toFixed(0);
                    }
                    return (unitAmount / 100).toFixed(2);
                } else {
                    console.warn(`unit_amount is null/undefined for ${lowerCaseCurrency}, using fallback`);
                }
            } else {
                console.log(`Currency options or ${lowerCaseCurrency} key not found in productData`);
                console.log(`Available currency options:`, Object.keys(productData.currency_options || {}));
            }
        }

        // Fallback to default prices (should match what you set in Stripe dashboard defaults)
        // Using approximate exchange rates based on current rates
        const defaults: Record<string, Record<string, string>> = {
            'USD': {
                'prod_Ty04ZeXM4KxXye': '3.99',
                'prod_Ty0424C32KFqLy': '8.99',
                'prod_Ty0478ZZqPUJLY': '17.99',
                'prod_Ty04xT4QjBaRDL': '24.99'
            },
            'EUR': {  // ~1.08 EUR per USD
                'prod_Ty04ZeXM4KxXye': '3.59',
                'prod_Ty0424C32KFqLy': '8.09',
                'prod_Ty0478ZZqPUJLY': '15.35',
                'prod_Ty04xT4QjBaRDL': '21.47'
            },
            'JPY': {  // ~150 JPY per USD
                'prod_Ty04ZeXM4KxXye': '599',
                'prod_Ty0424C32KFqLy': '1348',
                'prod_Ty0478ZZqPUJLY': '2698',
                'prod_Ty04xT4QjBaRDL': '3748'
            },
            'CNY': {  // ~7.1 CNY per USD
                'prod_Ty04ZeXM4KxXye': '28.33',
                'prod_Ty0424C32KFqLy': '63.74',
                'prod_Ty0478ZZqPUJLY': '127.69',
                'prod_Ty04xT4QjBaRDL': '177.75'
            },
            'AUD': {  // ~1.5 AUD per USD
                'prod_Ty04ZeXM4KxXye': '5.98',
                'prod_Ty0424C32KFqLy': '13.48',
                'prod_Ty0478ZZqPUJLY': '26.98',
                'prod_Ty04xT4QjBaRDL': '37.48'
            },
            'GBP': {  // ~0.82 GBP per USD
                'prod_Ty04ZeXM4KxXye': '3.26',
                'prod_Ty0424C32KFqLy': '7.34',
                'prod_Ty0478ZZqPUJLY': '14.71',
                'prod_Ty04xT4QjBaRDL': '20.47'
            }
        };

        const currencyDefaults = defaults[selectedCurrency] || defaults['USD'];
        console.log(`Using fallback price for ${productId} in ${selectedCurrency}:`, currencyDefaults[productId] || '0.00');
        return currencyDefaults[productId] || '0.00';
    };

    return [
        {
            id: 'prod_Ty04ZeXM4KxXye',
            name: '50 Credits',
            description: `50 Credits for ${currencySymbol}${getPrice('prod_Ty04ZeXM4KxXye')} (1 credit = 1 photo) to be used whenever you like.`,
            price: `${currencySymbol}${getPrice('prod_Ty04ZeXM4KxXye')}`
        },
        {
            id: 'prod_Ty0424C32KFqLy',
            name: '200 Credits',
            description: `200 Credits for ${currencySymbol}${getPrice('prod_Ty0424C32KFqLy')} (1 credit = 1 photo) to be used whenever you like.`,
            price: `${currencySymbol}${getPrice('prod_Ty0424C32KFqLy')}`
        },
        {
            id: 'prod_Ty0478ZZqPUJLY',
            name: '800 Credits',
            description: `800 Credits for ${currencySymbol}${getPrice('prod_Ty0478ZZqPUJLY')} (1 credit = 1 photo) to be used whenever you like.`,
            price: `${currencySymbol}${getPrice('prod_Ty0478ZZqPUJLY')}`
        },
        {
            id: 'prod_Ty04xT4QjBaRDL',
            name: '800 Credits/Month',
            description: `${currencySymbol}${getPrice('prod_Ty04xT4QjBaRDL')}/month - Get 800 credits every month. Keep all your images on the servers to view on the map and download anytime you like (download cap at 20GB/month).`,
            price: `${currencySymbol}${getPrice('prod_Ty04xT4QjBaRDL')}/month`
        }
    ];
};

interface Product {
    id: string;
    name: string;
    description: string;
    price: string;
}

interface PurchaseModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const PurchaseModal: React.FC<PurchaseModalProps> = ({ isOpen, onClose }) => {
    const [pricingData, setPricingData] = useState<PricingData | null>(null);
    const [selectedCurrency, setSelectedCurrency] = useState<string>('USD');
    const [showCurrencyDropdown, setShowCurrencyDropdown] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [paymentFailure, setPaymentFailure] = useState<string>('');

    // Detect user location for default currency
    useEffect(() => {
        // First, try timezone detection (works without GPS permission)
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        let detectedCurrency = 'USD';

        if (tz.includes('Europe') || tz === 'Europe/Rome' || tz.startsWith('Europe/')) {
            detectedCurrency = 'EUR';
        } else if (tz.includes('Japan') || tz === 'Asia/Tokyo') {
            detectedCurrency = 'JPY';
        } else if (tz.includes('China') || tz === 'Asia/Shanghai') {
            detectedCurrency = 'CNY';
        } else if (tz.includes('Australia') || tz === 'Australia/Sydney') {
            detectedCurrency = 'AUD';
        } else if (tz.includes('UK') || tz.includes('London') || tz === 'Europe/London') {
            detectedCurrency = 'GBP';
        }

        setSelectedCurrency(detectedCurrency);
        console.log(`Detected timezone: ${tz}, setting currency to ${detectedCurrency}`);

        // Also try geolocation if available (for potentially more accurate detection)
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                () => {
                    // GPS available - could potentially use coordinates for more precise detection
                    console.log('Geolocation successful, using timezone-based currency detection');
                },
                () => {
                    console.log('Geolocation permission denied or unavailable, timezone detection already applied');
                }
            );
        }
    }, []);

    // Fetch pricing data when modal opens
    useEffect(() => {
        if (isOpen) {
            if (!pricingData) {
                fetchPricingData();
            }
            checkPaymentStatus();
        }
    }, [isOpen, pricingData]);

    const fetchPricingData = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await authService.getPricingData();
            if (!isPricingData(response)) {
                throw new Error('Invalid pricing data');
            }
            console.log('Pricing data received:', response); // Debug log
            console.log('Products structure:', response.products); // Debug log
            setPricingData(response);

            // Frontend timezone detection takes precedence - don't override with backend response
            // Backend returns USD fallback, but we use frontend timezone detection instead
        } catch (err) {
            console.error('Error fetching pricing data:', err);
            setError('Unable to load pricing information. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const checkPaymentStatus = async () => {
        try {
            const status = await authService.getPaymentStatus();
            const latest = status?.latest_failure;
            if (latest?.message) {
                setPaymentFailure(latest.message);
            }
        } catch (err) {
            console.error('Error checking payment status:', err);
        }
    };

    const handleCurrencyChange = (currency: string) => {
        setSelectedCurrency(currency);
        setShowCurrencyDropdown(false);
    };

    const handlePurchase = async (productId: string) => {
        try {
            setLoading(true);
            const checkoutUrl = await authService.createCheckoutSession(productId, selectedCurrency);
            // Instead of calling onPurchase directly, redirect to checkout
            window.location.href = checkoutUrl;
        } catch (error) {
            console.error('Error creating checkout session:', error);
            setError('Unable to process purchase. Please try again.');
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isOpen) {
            setPaymentFailure('');
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const products = getDynamicProducts(selectedCurrency, pricingData);

    return (
        <div className="purchase-modal-overlay">
            <div className="purchase-modal-content">
                <div className="purchase-modal-header">
                    <h2>Buy Credits</h2>
                    <button onClick={onClose} className="close-modal-button">&times;</button>
                </div>

                {paymentFailure && (
                    <div className="payment-failure-banner">
                        Payment failed. Please try again or use another method. ({paymentFailure})
                    </div>
                )}

                {/* Currency Selector */}
                <div className="currency-selector">
                    <button
                        className="currency-button"
                        onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                    >
                        Currency: {selectedCurrency} {currencySymbols[selectedCurrency]}
                        {showCurrencyDropdown ? ' ▲' : ' ▼'}
                    </button>

                    {showCurrencyDropdown && (
                        <div className="currency-dropdown">
                            {pricingData?.supported_currencies?.map((currency: string) => (
                                <div
                                    key={currency}
                                    className={`currency-option ${currency === selectedCurrency ? 'selected' : ''}`}
                                    onClick={() => handleCurrencyChange(currency)}
                                >
                                    {currency} {currencySymbols[currency]}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {error && <div className="error-message">{error}</div>}

                {loading ? (
                    <div className="loading-message">Loading pricing information...</div>
                ) : (
                    <div className="products-grid">
                        {products.map((product) => (
                            <div key={product.id} className="product-card">
                                <h3>{product.name}</h3>
                                <p className="product-price">{product.price}</p>
                                <p className="product-description">{product.description}</p>
                                <button
                                    onClick={() => handlePurchase(product.id)}
                                    className="purchase-product-button"
                                    disabled={loading}
                                >
                                    {loading ? 'Processing...' : 'Purchase'}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PurchaseModal;
